import React, { useState, useEffect } from 'react';
import { defaultUserSettings } from './data/userSettings';
import Header from './components/Header';
import Progress from './components/Progress';
import VocabularyCard from './components/VocabularyCard';
import Settings from './components/Settings';
import Calendar from './components/Calendar';
import VocabularyBrowser from './components/VocabularyBrowser';
import learningScheduler from './services/learningScheduler';
import pronunciationService from './services/pronunciationService';
import { getWordTextByPreference, getTitleByPreference } from './utils/chineseCharacterUtils';

const VocabRight = () => {
  // Application state
  const [currentMode, setCurrentMode] = useState('home');
  const [currentSet, setCurrentSet] = useState(0);
  const [currentWord, setCurrentWord] = useState(0);
  const [showAnswer, setShowAnswer] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);
  const [showBrowser, setShowBrowser] = useState(false);
  const [settings, setSettings] = useState(defaultUserSettings);
  const [progress, setProgress] = useState(defaultUserSettings.progress);
  const [todaySets, setTodaySets] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dataLoaded, setDataLoaded] = useState(false);

  // Load settings and progress from localStorage on component mount
  // Also load vocabulary sets from Supabase
  useEffect(() => {
    const initializeApp = async () => {
      try {
        console.log('VocabRight component mounting...');
        
        // Load settings and progress from localStorage
        const savedSettings = localStorage.getItem('vocabRightSettings');
        const savedProgress = localStorage.getItem('vocabRightProgress');
        
        console.log('Saved settings:', savedSettings);
        console.log('Saved progress:', savedProgress);
        
        if (savedSettings) {
          const parsedSettings = JSON.parse(savedSettings);
          // Ensure all required properties exist
          const completeSettings = {
            ...defaultUserSettings,
            ...parsedSettings,
            learningSchedule: {
              ...defaultUserSettings.learningSchedule,
              ...(parsedSettings.learningSchedule || {})
            },
            pronunciation: {
              ...defaultUserSettings.pronunciation,
              ...(parsedSettings.pronunciation || {})
            }
          };
          setSettings(completeSettings);
        }
        if (savedProgress) {
          setProgress(JSON.parse(savedProgress));
        }

        // Load vocabulary sets from Supabase
        console.log('Loading vocabulary sets from Supabase...');
        await learningScheduler.loadSets();
        setDataLoaded(true);
        
        console.log('Component loaded successfully');
        setIsLoading(false);
      } catch (err) {
        console.error('Error initializing app:', err);
        setError(err.message);
        setIsLoading(false);
      }
    };

    initializeApp();
  }, []);

  // Update today's sets when settings change or data is loaded
  useEffect(() => {
    const loadTodaySets = async () => {
      if (!dataLoaded) return; // Wait for data to load first
      
      try {
        console.log('Loading today sets with settings:', settings);
        const sets = await learningScheduler.getTodaySets(settings);
        console.log('Loaded today sets:', sets);
        setTodaySets(sets);
      } catch (err) {
        console.error('Error loading today sets:', err);
        setError(err.message);
      }
    };

    loadTodaySets();
  }, [settings, dataLoaded]);

  // Save settings to localStorage
  const saveSettings = (newSettings) => {
    setSettings(newSettings);
    localStorage.setItem('vocabRightSettings', JSON.stringify(newSettings));
  };

  // Save progress to localStorage
  const saveProgress = (newProgress) => {
    setProgress(newProgress);
    localStorage.setItem('vocabRightProgress', JSON.stringify(newProgress));
  };

  // Update calendar data
  const updateCalendarData = (date, status, setId, score = null) => {
    const newProgress = {
      ...progress,
      calendarData: {
        ...progress.calendarData,
        [date]: { status, setId, score }
      }
    };
    saveProgress(newProgress);
  };

  // Handle pronunciation
  const handlePronunciation = (word) => {
    if (!settings.pronunciation.enabled) return;
    
    const text = pronunciationService.getPronunciationText(word, settings.pronunciation.language);
    pronunciationService.speak(text, settings.pronunciation.language, {
      volume: settings.pronunciation.volume
    });
  };

  // Get current vocabulary set
  const getCurrentVocabularySet = () => {
    if (currentMode === 'home' && todaySets.length > 0) {
      return todaySets[0]; // Default to first today's set
    }
    if (learningScheduler.allSets && learningScheduler.allSets.length > currentSet) {
      return learningScheduler.allSets[currentSet];
    }
    return null; // Return null if not loaded yet
  };

  const currentVocabularySet = getCurrentVocabularySet();
  
  // Don't render if current set is not available
  if (currentVocabularySet === null && currentMode !== 'home') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-orange-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading vocabulary set...</p>
        </div>
      </div>
    );
  }

  // Recommended Sets Component (separate to handle async)
  const RecommendedSetsSection = ({ settings, onSetSelect }) => {
    const [recommendedSets, setRecommendedSets] = useState([]);
    const [isLoadingRecs, setIsLoadingRecs] = useState(true);

    useEffect(() => {
      const loadRecommended = async () => {
        if (!dataLoaded) return;
        try {
          const sets = await learningScheduler.getRecommendedSets(settings);
          setRecommendedSets(sets.slice(0, 3));
          setIsLoadingRecs(false);
        } catch (err) {
          console.error('Error loading recommended sets:', err);
          setIsLoadingRecs(false);
        }
      };
      loadRecommended();
    }, [settings, dataLoaded]);

    if (isLoadingRecs) {
      return (
        <div>
          <h2 className="text-xl font-bold text-gray-800 mb-4">Recommended Sets</h2>
          <div className="text-center py-4 text-gray-500">Loading recommendations...</div>
        </div>
      );
    }

    return (
      <div>
        <h2 className="text-xl font-bold text-gray-800 mb-4">Recommended Sets</h2>
        <div className="grid grid-cols-1 gap-4">
          {recommendedSets.map((set, index) => {
            const title = getTitleByPreference(set, settings.chineseCharacterPreference);
            return (
              <div 
                key={set.id}
                className="bg-white rounded-xl p-4 shadow-sm cursor-pointer hover:shadow-md transition-shadow"
                onClick={() => {
                  onSetSelect(set, set.originalIndex);
                }}
              >
                <div className="flex items-center">
                  <img 
                    src={set.imageUrl} 
                    alt={title}
                    className="w-12 h-12 rounded-lg object-cover mr-4"
                  />
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-800">{title}</h3>
                    <p className="text-sm text-gray-600">{set.titleEnglish}</p>
                  </div>
                  <div className="text-sm text-gray-500">{set.words.length} words</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Home page component
  const HomePage = () => {
    const stats = learningScheduler.getProgressStats(progress);
    
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-orange-50">
        <Header 
          title="VocabRight"
          subtitle="每日记单词 - Daily Vocabulary Learning"
          showSettings={true}
          onSettings={() => setShowSettings(true)}
          backgroundImage="https://images.unsplash.com/photo-1518709268805-4e9042af2176?w=800&h=400&fit=crop"
          className="mb-6"
        />
        
        <div className="px-4 pb-8">
          {/* Progress Section */}
          <Progress progress={progress} className="mb-6" />
          
          {/* Quick Stats */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="bg-white rounded-xl p-4 shadow-sm">
              <div className="text-2xl font-bold text-blue-600">{stats.currentStreak}</div>
              <div className="text-sm text-gray-600">Day Streak</div>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-sm">
              <div className="text-2xl font-bold text-green-600">{stats.successRate}%</div>
              <div className="text-sm text-gray-600">Success Rate</div>
            </div>
          </div>

          {/* Today's Sets */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-800">Today's Vocabulary Sets</h2>
              <button
                onClick={() => setShowCalendar(true)}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                View Calendar
              </button>
            </div>
            
            {todaySets.length > 0 ? (
              <div className="space-y-4">
                {todaySets.map((set, index) => {
                  const title = getTitleByPreference(set, settings.chineseCharacterPreference);
                  return (
                    <div 
                      key={set.id}
                      className="bg-white rounded-xl p-6 shadow-sm cursor-pointer hover:shadow-md transition-shadow"
                      onClick={() => {
                        setCurrentSet(set.originalIndex);
                        setCurrentMode('read'); // Go directly to Read Mode
                      }}
                    >
                      <div className="flex items-center">
                        <img 
                          src={set.imageUrl} 
                          alt={title}
                          className="w-16 h-16 rounded-lg object-cover mr-4"
                        />
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold text-gray-800">
                            {title}
                          </h3>
                          <p className="text-gray-600">{set.titleEnglish}</p>
                          <p className="text-sm text-gray-500">8 words to learn today</p>
                        </div>
                        <div className="text-right">
                          <div className="text-sm text-gray-500">{set.assignmentType}</div>
                          {(set.studyMode || set.testMode) && (
                            <div className="text-xs text-blue-600 mt-1">Study Mode</div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="bg-white rounded-xl p-6 shadow-sm text-center text-gray-500">
                No sets assigned for today. Check your learning schedule settings.
              </div>
            )}
          </div>

          {/* Quick Actions */}
          <div className="grid grid-cols-2 gap-4 mb-8">
            <button
              onClick={() => setShowBrowser(true)}
              className="bg-white rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow text-left"
            >
              <div className="text-lg font-semibold text-gray-800 mb-1">Browse All Sets</div>
              <div className="text-sm text-gray-600">Explore 195 vocabulary sets</div>
            </button>
            
            <button
              onClick={() => setShowCalendar(true)}
              className="bg-white rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow text-left"
            >
              <div className="text-lg font-semibold text-gray-800 mb-1">Learning Calendar</div>
              <div className="text-sm text-gray-600">Track your progress</div>
            </button>
          </div>

          {/* Recommended Sets */}
          <RecommendedSetsSection settings={settings} onSetSelect={(set, index) => {
            setCurrentSet(set.originalIndex);
            setCurrentMode('read'); // Go directly to Read Mode
          }} />
        </div>
      </div>
    );
  };

  // Mode selector component
  const ModeSelector = () => {
    const title = getTitleByPreference(currentVocabularySet, settings.chineseCharacterPreference);
    
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-orange-50">
        <Header 
          title={title}
          subtitle={currentVocabularySet.titleEnglish}
          showBack={true}
          showSettings={true}
          onBack={() => setCurrentMode('home')}
          onSettings={() => setShowSettings(true)}
          backgroundImage={currentVocabularySet.imageUrl}
          className="mb-6"
        />
        
        <div className="px-4 pb-8">
          <div className="grid grid-cols-1 gap-4">
            <div 
              className="bg-white rounded-xl p-6 shadow-sm cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => setCurrentMode('read')}
            >
              <div className="flex items-center">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mr-4">
                  <span className="text-2xl">📖</span>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-800">Read Mode</h3>
                  <p className="text-gray-600">Browse and review vocabulary</p>
                </div>
              </div>
            </div>

            <div 
              className="bg-white rounded-xl p-6 shadow-sm cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => setCurrentMode('study')}
            >
              <div className="flex items-center">
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mr-4">
                  <span className="text-2xl">🎯</span>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-800">Study Mode</h3>
                  <p className="text-gray-600">Interactive flashcards</p>
                </div>
              </div>
            </div>

            <div 
              className="bg-white rounded-xl p-6 shadow-sm cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => setCurrentMode('test')}
            >
              <div className="flex items-center">
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mr-4">
                  <span className="text-2xl">📝</span>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-800">Test Mode</h3>
                  <p className="text-gray-600">Quiz yourself</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Read mode component
  const ReadMode = () => {
    const title = getTitleByPreference(currentVocabularySet, settings.chineseCharacterPreference);
    
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-orange-50">
        <Header 
          title={title}
          subtitle={currentVocabularySet.titleEnglish}
          showBack={true}
          showSettings={true}
          onBack={() => setCurrentMode('home')}
          onSettings={() => setShowSettings(true)}
          backgroundImage={currentVocabularySet.imageUrl}
          className="mb-6"
        />
        
        <div className="px-4 pb-8">
          {/* Mode Switching Buttons */}
          <div className="flex gap-3 mb-6 justify-center">
            <button
              onClick={() => setCurrentMode('read')}
              className="px-4 py-2 bg-blue-500 text-white rounded-lg font-semibold shadow-sm"
            >
              📖 Read Mode
            </button>
            <button
              onClick={() => {
                setCurrentWord(0);
                setShowAnswer(false);
                setCurrentMode('study');
              }}
              className="px-4 py-2 bg-green-500 text-white rounded-lg font-semibold shadow-sm hover:bg-green-600"
            >
              🎯 Study Mode
            </button>
            <button
              onClick={() => {
                setCurrentWord(0);
                setCurrentMode('test');
              }}
              className="px-4 py-2 bg-purple-500 text-white rounded-lg font-semibold shadow-sm hover:bg-purple-600"
            >
              📝 Test Mode
            </button>
          </div>

          <div className="space-y-4">
            {currentVocabularySet.words.map((word, index) => (
              <VocabularyCard
                key={word.id}
                word={word}
                languageDisplay={settings.languageDisplay}
                chineseCharacterPreference={settings.chineseCharacterPreference}
                index={index}
                showImage={settings.studyPreferences.showImages}
                showPhonetics={settings.studyPreferences.showPhonetics}
                onClick={() => handlePronunciation(word)}
                onPronunciation={handlePronunciation}
                className="fade-in"
              />
            ))}
          </div>
        </div>
      </div>
    );
  };

  // Study mode component
  const StudyMode = () => {
    const currentWordData = currentVocabularySet.words[currentWord];
    const title = getTitleByPreference(currentVocabularySet, settings.chineseCharacterPreference);
    
    // Get display text using the utility function
    const displayText = getWordTextByPreference(currentWordData, settings.languageDisplay, settings.chineseCharacterPreference);

    const handleNext = () => {
      if (currentWord < currentVocabularySet.words.length - 1) {
        setCurrentWord(currentWord + 1);
        setShowAnswer(false);
      } else {
        // Completed the set
        const today = new Date().toISOString().split('T')[0];
        updateCalendarData(today, 'DS', currentVocabularySet.id, 100);
        
        const newProgress = {
          ...progress,
          totalWordsLearned: progress.totalWordsLearned + currentVocabularySet.words.length,
          setsCompleted: progress.setsCompleted + 1
        };
        saveProgress(newProgress);
        setCurrentMode('home');
      }
    };

    // Auto-play pronunciation if enabled
    useEffect(() => {
      if (settings.pronunciation.autoPlay && settings.pronunciation.enabled) {
        handlePronunciation(currentWordData);
      }
    }, [currentWord]);

    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-orange-50">
        <Header 
          title={title}
          subtitle={currentVocabularySet.titleEnglish}
          showBack={true}
          showSettings={true}
          onBack={() => setCurrentMode('read')}
          onSettings={() => setShowSettings(true)}
          backgroundImage={currentVocabularySet.imageUrl}
          className="mb-6"
        />
        
        <div className="px-4 pb-8">
          {/* Mode Switching Buttons */}
          <div className="flex gap-3 mb-6 justify-center">
            <button
              onClick={() => setCurrentMode('read')}
              className="px-4 py-2 bg-blue-500 text-white rounded-lg font-semibold shadow-sm hover:bg-blue-600"
            >
              📖 Read Mode
            </button>
            <button
              onClick={() => {
                setCurrentWord(0);
                setShowAnswer(false);
                setCurrentMode('study');
              }}
              className="px-4 py-2 bg-green-500 text-white rounded-lg font-semibold shadow-sm"
            >
              🎯 Study Mode
            </button>
            <button
              onClick={() => {
                setCurrentWord(0);
                setCurrentMode('test');
              }}
              className="px-4 py-2 bg-purple-500 text-white rounded-lg font-semibold shadow-sm hover:bg-purple-600"
            >
              📝 Test Mode
            </button>
          </div>
          {/* Progress indicator */}
          <div className="mb-6">
            <div className="flex justify-between text-sm text-gray-600 mb-2">
              <span>Word {currentWord + 1} of {currentVocabularySet.words.length}</span>
              <span>{Math.round(((currentWord + 1) / currentVocabularySet.words.length) * 100)}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-orange-500 h-2 rounded-full transition-all duration-300"
                style={{ width: `${((currentWord + 1) / currentVocabularySet.words.length) * 100}%` }}
              ></div>
            </div>
          </div>

          {/* Flashcard */}
          <div 
            className="bg-white rounded-xl p-8 shadow-lg cursor-pointer hover:shadow-xl transition-shadow mb-6"
            onClick={() => setShowAnswer(!showAnswer)}
          >
            <div className="text-center">
              <div className="text-3xl font-bold text-gray-800 mb-4">
                {displayText.primary}
              </div>
              
              {showAnswer && (
                <div className="space-y-4">
                  <div className="text-2xl font-semibold text-blue-600">
                    {displayText.secondary}
                  </div>
                  {settings.studyPreferences.showPhonetics && displayText.phonetic && (
                    <div className="text-lg text-gray-500 font-mono">
                      {displayText.phonetic}
                    </div>
                  )}
                  {settings.studyPreferences.showImages && currentWordData.imageUrl && (
                    <img 
                      src={currentWordData.imageUrl} 
                      alt={displayText.primary}
                      className="w-32 h-32 rounded-lg object-cover mx-auto"
                    />
                  )}
                </div>
              )}
              
              {!showAnswer && (
                <div className="text-gray-500">
                  Tap to reveal answer
                </div>
              )}
            </div>
          </div>

          {/* Pronunciation button */}
          {settings.pronunciation.enabled && (
            <div className="text-center mb-6">
              <button
                onClick={() => handlePronunciation(currentWordData)}
                className="px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
              >
                🔊 Pronounce
              </button>
            </div>
          )}

          {/* Navigation buttons */}
          <div className="flex justify-between">
            <button
              onClick={() => {
                if (currentWord > 0) {
                  setCurrentWord(currentWord - 1);
                  setShowAnswer(false);
                }
              }}
              disabled={currentWord === 0}
              className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            
            <button
              onClick={handleNext}
              className="px-6 py-3 bg-orange-500 text-white rounded-lg hover:bg-orange-600"
            >
              {currentWord === currentVocabularySet.words.length - 1 ? 'Complete' : 'Next'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Test mode component
  const TestMode = () => {
    const [currentQuestion, setCurrentQuestion] = useState(0);
    const [score, setScore] = useState(0);
    const [showResults, setShowResults] = useState(false);
    const title = getTitleByPreference(currentVocabularySet, settings.chineseCharacterPreference);
    
    // Generate questions for the current set
    const generateQuestions = () => {
      const questions = currentVocabularySet.words.map((word, index) => {
        const getQuestionText = () => {
          const displayText = getWordTextByPreference(word, settings.languageDisplay, settings.chineseCharacterPreference);
          return { 
            question: displayText.primary, 
            answer: displayText.secondary 
          };
        };

        const { question, answer } = getQuestionText();
        
        // Generate wrong answers from other words in the set
        const wrongAnswers = currentVocabularySet.words
          .filter((_, i) => i !== index)
          .map(w => {
            const displayText = getWordTextByPreference(w, settings.languageDisplay, settings.chineseCharacterPreference);
            return displayText.secondary;
          })
          .slice(0, 3);

        const options = [answer, ...wrongAnswers].sort(() => Math.random() - 0.5);
        
        return {
          question,
          answer,
          options,
          word: word
        };
      });

      return questions.sort(() => Math.random() - 0.5);
    };

    const [questions] = useState(generateQuestions());
    const currentQ = questions[currentQuestion];

    const handleAnswer = (selectedAnswer) => {
      const isCorrect = selectedAnswer === currentQ.answer;
      if (isCorrect) {
        setScore(score + 1);
      }

      if (currentQuestion < questions.length - 1) {
        setCurrentQuestion(currentQuestion + 1);
      } else {
        setShowResults(true);
        const accuracy = Math.round((score + (isCorrect ? 1 : 0)) / questions.length * 100);
        
        // Update calendar data
        const today = new Date().toISOString().split('T')[0];
        const status = accuracy >= 70 ? 'DS' : 'DF';
        updateCalendarData(today, status, currentVocabularySet.id, accuracy);
        
        const newProgress = {
          ...progress,
          accuracy: Math.max(progress.accuracy, accuracy)
        };
        saveProgress(newProgress);
      }
    };

    if (showResults) {
      const finalScore = score + (currentQ.answer === currentQ.answer ? 1 : 0);
      const accuracy = Math.round(finalScore / questions.length * 100);
      
      return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-orange-50">
          <Header 
            title={title}
            subtitle={currentVocabularySet.titleEnglish}
            showBack={true}
            showSettings={true}
            onBack={() => setCurrentMode('read')}
            onSettings={() => setShowSettings(true)}
            backgroundImage={currentVocabularySet.imageUrl}
            className="mb-6"
          />
          
          <div className="px-4 pb-8">
            <div className="bg-white rounded-xl p-8 shadow-lg text-center">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">Test Complete!</h2>
              <div className="text-6xl font-bold text-orange-500 mb-4">{accuracy}%</div>
              <p className="text-gray-600 mb-6">
                You got {finalScore} out of {questions.length} questions correct
              </p>
              <div className="flex gap-3 justify-center">
                <button
                  onClick={() => setCurrentMode('read')}
                  className="px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
                >
                  📖 Back to Read Mode
                </button>
                <button
                  onClick={() => setCurrentMode('home')}
                  className="px-6 py-3 bg-orange-500 text-white rounded-lg hover:bg-orange-600"
                >
                  Back to Home
                </button>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-orange-50">
        <Header 
          title={title}
          subtitle={currentVocabularySet.titleEnglish}
          showBack={true}
          showSettings={true}
          onBack={() => setCurrentMode('read')}
          onSettings={() => setShowSettings(true)}
          backgroundImage={currentVocabularySet.imageUrl}
          className="mb-6"
        />
        
        <div className="px-4 pb-8">
          {/* Mode Switching Buttons */}
          <div className="flex gap-3 mb-6 justify-center">
            <button
              onClick={() => setCurrentMode('read')}
              className="px-4 py-2 bg-blue-500 text-white rounded-lg font-semibold shadow-sm hover:bg-blue-600"
            >
              📖 Read Mode
            </button>
            <button
              onClick={() => {
                setCurrentWord(0);
                setShowAnswer(false);
                setCurrentMode('study');
              }}
              className="px-4 py-2 bg-green-500 text-white rounded-lg font-semibold shadow-sm hover:bg-green-600"
            >
              🎯 Study Mode
            </button>
            <button
              onClick={() => {
                setCurrentWord(0);
                setCurrentMode('test');
              }}
              className="px-4 py-2 bg-purple-500 text-white rounded-lg font-semibold shadow-sm"
            >
              📝 Test Mode
            </button>
          </div>
          {/* Progress indicator */}
          <div className="mb-6">
            <div className="flex justify-between text-sm text-gray-600 mb-2">
              <span>Question {currentQuestion + 1} of {questions.length}</span>
              <span>Score: {score}</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-purple-500 h-2 rounded-full transition-all duration-300"
                style={{ width: `${((currentQuestion + 1) / questions.length) * 100}%` }}
              ></div>
            </div>
          </div>

          {/* Question */}
          <div className="bg-white rounded-xl p-8 shadow-lg mb-6">
            <h3 className="text-2xl font-bold text-gray-800 mb-6 text-center">
              {currentQ.question}
            </h3>
            
            <div className="space-y-4">
              {currentQ.options.map((option, index) => (
                <button
                  key={index}
                  onClick={() => handleAnswer(option)}
                  className="w-full p-4 text-left bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  {option}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render current mode
  const renderCurrentMode = () => {
    switch (currentMode) {
      case 'home':
        return <HomePage />;
      case 'read':
        return <ReadMode />;
      case 'study':
        return <StudyMode />;
      case 'test':
        return <TestMode />;
      default:
        return <HomePage />;
    }
  };

  // Handle loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-orange-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading VocabRight...</p>
        </div>
      </div>
    );
  }

  // Handle error state
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-orange-50 flex items-center justify-center">
        <div className="text-center p-8 bg-white rounded-lg shadow-lg">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h2 className="text-xl font-bold text-gray-800 mb-2">Something went wrong</h2>
          <p className="text-gray-600 mb-4">Error: {error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600"
          >
            Reload App
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      {renderCurrentMode()}
      
      {/* Settings Modal */}
      {showSettings && (
        <Settings
          settings={settings}
          onSettingsChange={saveSettings}
          onClose={() => setShowSettings(false)}
        />
      )}

      {/* Calendar Modal */}
      {showCalendar && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl w-full max-w-4xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b">
              <h2 className="text-xl font-bold text-gray-800">Learning Calendar</h2>
              <button
                onClick={() => setShowCalendar(false)}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
              >
                ✕
              </button>
            </div>
            <div className="p-6">
              <Calendar 
                calendarData={progress.calendarData || {}}
                onDateClick={(day) => {
                  console.log('Date clicked:', day);
                  // Could show details for that day
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Vocabulary Browser Modal */}
      {showBrowser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl w-full max-w-6xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b">
              <h2 className="text-xl font-bold text-gray-800">Vocabulary Browser</h2>
              <button
                onClick={() => setShowBrowser(false)}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
              >
                ✕
              </button>
            </div>
            <div className="p-6">
              <VocabularyBrowser 
                userSettings={settings}
                onSetSelect={(set, index) => {
                  // Use originalIndex to find the correct set in allSets array
                  const actualIndex = learningScheduler.allSets.findIndex(s => s.id === set.id);
                  setCurrentSet(actualIndex !== -1 ? actualIndex : set.originalIndex || 0);
                  setShowBrowser(false);
                  setCurrentMode('read'); // Go directly to Read Mode
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VocabRight;
